import { ChangeEvent, FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, FormControl, FormLabel, TextField, Typography, IconButton, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import SliderBG from "../../assets/images/Login/SliderBG.png";
import { logInWithEmailAndPassword } from "../../firebase/firebase";
import APP_ROUTES from "../../routers/routeConstants";
import { LoginLogoIcon } from "../../assets/icons/MUI/LoginIcons";

import QuotesSlider from "./QuotesSlider";

const PASSWORD_REGEX = /^[A-Za-z0-9!@#$%^&*()\-_=+\[\]{};:,?.]+$/;
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const myQueryClient = useQueryClient();

  const [formData, setFormData] = useState({
    emailAddress: "",
    password: "",
  });
  const [errors, setErrors] = useState({
    emailAddress: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const sanitizedValue = value.replace(/\s/g, "");
    setFormData({ ...formData, [name]: sanitizedValue });
    setErrors({ ...errors, [name]: "" });
  };

  const validateForm = () => {
    let valid = true;
    let newErrors = {
      emailAddress: "",
      password: "",
    };

    if (!formData.emailAddress || !EMAIL_REGEX.test(formData.emailAddress)) {
      newErrors.emailAddress = t("pages.login.validEmailRequired");
      valid = false;
    } else if (formData.emailAddress.length > 254) {
      newErrors.emailAddress = t("pages.login.emailMaxLength");
      valid = false;
    }

    if (!formData.password) {
      newErrors.password = t("pages.login.passwordEmpty");
      valid = false;
    } else if (formData.password.length < 8) {
      newErrors.password = t("pages.login.passwordMinLength");
      valid = false;
    } else if (formData.password.length > 128) {
      newErrors.password = t("pages.login.passwordMaxLength");
      valid = false;
    } else if (!PASSWORD_REGEX.test(formData.password)) {
      newErrors.password = t("pages.login.passwordInvalidCharacters");
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      const loginResult = await logInWithEmailAndPassword(formData.emailAddress, formData.password);

      if (loginResult.success) {
        myQueryClient.clear();
        myQueryClient.invalidateQueries();

        navigate(APP_ROUTES.DASHBOARD);
      } else {
        toast.error(t(`pages.login.${loginResult.error}`) || "An unknown error occurred during login.");
      }
    } catch (error: any) {
      toast.error("An unexpected error occurred.");
    }
  };

  return (
    <Box sx={styles.container} component="main">
      <Box sx={styles.loginContainer}>
        <LoginLogoIcon />
        <Box>
          <Typography component="h5" sx={styles.welcomeBackTitle}>
            {t("pages.login.welcomeBack")}
          </Typography>
          <Typography component="h6" sx={styles.enterYourDetails}>
            {t("pages.login.enterYourDetails")}
          </Typography>
          <Box component="form" autoComplete="off" onSubmit={handleSubmit} sx={styles.formContainer}>
            <FormControl sx={styles.formControlStyle}>
              <FormLabel sx={styles.formLabelStyle}>{t("pages.login.emailAddress")}</FormLabel>
              <TextField
                inputProps={{
                  autocomplete: "new-password",
                  form: {
                    autocomplete: "off",
                  },
                }}
                name="emailAddress"
                value={formData.emailAddress}
                onChange={handleChange}
                placeholder={t("pages.login.enterEmail")}
                error={!!errors.emailAddress}
                helperText={errors.emailAddress}
                sx={styles.textFieldStyle}
              />
            </FormControl>
            <FormControl sx={styles.formControlStyle}>
              <FormLabel sx={styles.formLabelStyle}>{t("pages.login.password")}</FormLabel>
              <TextField
                inputProps={{
                  autocomplete: "new-password",
                  form: {
                    autocomplete: "off",
                  },
                }}
                placeholder={t("pages.login.enterPassword")}
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                // onPaste={(e) => e.preventDefault()}
                error={!!errors.password}
                helperText={errors.password}
                sx={styles.textFieldStyle}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleTogglePasswordVisibility} edge="end">
                        {showPassword ? (
                          <VisibilityOff sx={{ color: "#A0A0A0" }} />
                        ) : (
                          <Visibility sx={{ color: "#A0A0A0" }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>
            <Button type="submit" variant="primary-lg" fullWidth sx={styles.submitButtonStyle}>
              {t("pages.login.login")}
            </Button>
          </Box>
        </Box>
      </Box>
      <Box sx={styles.quotesSliderContainer}>
        <QuotesSlider />
      </Box>
    </Box>
  );
};

export default Login;

const styles = {
  container: {
    background: "#141414",
    display: "flex",
    flexDirection: { xs: "column", lg: "row" },
    minHeight: "100vh",
  },
  quotesSliderContainer: {
    background: `url(${SliderBG})`,
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    backgroundPosition: "center",
    flex: 1,
    minHeight: { xs: "40vh", lg: "100vh" },
    display: { xs: "none", lg: "block" },
  },
  loginContainer: {
    minHeight: { xs: "100vh", lg: "100vh" },
    width: { xs: "100%", lg: "min(640px, 40vw)" },
    maxWidth: "100%",
    display: "flex",
    flexDirection: "column",
    rowGap: { xs: "40px", sm: "50px", lg: "60px" },
    justifyContent: "center",
    alignItems: "center",
    padding: { xs: "20px", sm: "40px", lg: "0" },
  },
  welcomeBackTitle: {
    color: "var(--White, #FFF)",
    fontFamily: "Montserrat",
    fontSize: { xs: "24px", sm: "28px", lg: "30px" },
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: { xs: "32px", sm: "36px", lg: "38px" },
    marginBottom: "12px",
    textAlign: "center",
  },
  enterYourDetails: {
    color: "var(--White, #FFF)",
    fontFamily: "Montserrat",
    fontSize: { xs: "14px", sm: "15px", lg: "16px" },
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: { xs: "20px", sm: "22px", lg: "24px" },
    marginBottom: "32px",
    textAlign: "center",
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
    rowGap: "20px",
    width: "100%",
    maxWidth: { xs: "100%", sm: "400px", lg: "360px" },
  },
  formLabelStyle: {
    color: "var(--White, #FFF)",
    fontFamily: "Montserrat",
    fontSize: { xs: "13px", sm: "14px" },
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },

  formControlStyle: {
    display: "flex",
    flexDirection: "column",
    rowGap: "6px",
  },

  textFieldStyle: {
    fontFamily: "Montserrat",
    fontSize: { xs: "13px", sm: "14px" },
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "24px",
    color: "#A0A0A0",
    width: "100%",
    "& .MuiInputBase-root": {
      border: "1px solid #1E2021 !important",
      background: "rgba(28, 28, 28, 0.80)",
      fontSize: { xs: "13px", sm: "14px" },
    },
    "& .MuiInputBase-input": {
      color: "#FFF",
      padding: { xs: "12px 14px", sm: "14px 16px" },
    },
  },
  submitButtonStyle: {
    color: "var(--Base-White, #FFF)",
    fontFamily: "Montserrat",
    fontSize: { xs: "14px", sm: "15px", lg: "16px" },
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
    border: "1px solid var(--Green---100, #008B07)",
    background: "var(--Green---100, #008B07)",
    padding: { xs: "12px", sm: "14px", lg: "16px" },
    minHeight: { xs: "48px", sm: "52px", lg: "56px" },
  },
};
